import { useEffect, useMemo, useRef, useState } from "react";
import { Check, ChevronDown, Clock, Search, X } from "lucide-react";

const RECENT_KEY = (scope) => `checkmate.recentEmployees.${scope}`;
const RECENT_MAX = 5;

export function pushRecentEmployee(scope, id) {
  if (!id) return;
  try {
    const key = RECENT_KEY(scope);
    const prev = JSON.parse(localStorage.getItem(key) || "[]").filter((x) => x !== id);
    localStorage.setItem(key, JSON.stringify([id, ...prev].slice(0, RECENT_MAX)));
  } catch { /* storage unavailable — recents are a convenience, not a requirement */ }
}

function readRecent(scope) {
  try { return JSON.parse(localStorage.getItem(RECENT_KEY(scope)) || "[]"); }
  catch { return []; }
}

const label = (e) => `${e.first_name || ""} ${e.last_name || ""}`.trim() || e.email || "—";

/**
 * Searchable employee combobox. Filters on first name, last name, employee ID
 * and email as you type; the most recently chosen employees stay pinned to the
 * top. One component so Timesheets and the Payroll wizard navigate identically.
 */
export default function EmployeeCombobox({
  employees = [],
  value = "",
  onChange,
  scope = "default",
  testid = "employee-combobox",
  placeholder = "Search name or employee ID…",
  emptyLabel = null,          // when set, offers a "clear" row with this label
  meta = null,                // optional (e) => string shown under each row
  disabled = false,
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [recent, setRecent] = useState(() => readRecent(scope));
  const boxRef = useRef(null);

  useEffect(() => {
    const onDoc = (ev) => { if (boxRef.current && !boxRef.current.contains(ev.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const selected = employees.find((e) => e.id === value) || null;

  const { recentRows, rows } = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const match = (e) => {
      if (!needle) return true;
      return [e.first_name, e.last_name, label(e), e.id, e.email]
        .filter(Boolean)
        .some((f) => String(f).toLowerCase().includes(needle));
    };
    const hits = employees.filter(match);
    const recentHits = recent
      .map((id) => hits.find((e) => e.id === id))
      .filter(Boolean);
    const recentIds = new Set(recentHits.map((e) => e.id));
    return { recentRows: recentHits, rows: hits.filter((e) => !recentIds.has(e.id)) };
  }, [employees, q, recent]);

  const pick = (id) => {
    if (id) { pushRecentEmployee(scope, id); setRecent(readRecent(scope)); }
    onChange(id);
    setOpen(false);
    setQ("");
  };

  const Row = ({ e, isRecent }) => (
    <button
      type="button"
      data-testid={`${testid}-option-${e.id}`}
      onClick={() => pick(e.id)}
      className="w-full text-left px-3 py-2 border-b border-ink/10 hover:bg-secondary flex items-start gap-2"
    >
      {isRecent && <Clock className="w-3 h-3 mt-1 shrink-0" />}
      <span className="flex-1 min-w-0">
        <span className="block font-mono text-xs font-black truncate">{label(e)}</span>
        <span className="block font-mono text-[10px] text-muted-foreground truncate">
          {meta ? meta(e) : `ID ${e.id}${e.email ? ` · ${e.email}` : ""}`}
        </span>
      </span>
      {e.id === value && <Check className="w-4 h-4 mt-0.5 shrink-0" />}
    </button>
  );

  return (
    <div className="relative min-w-[260px]" ref={boxRef}>
      <button
        type="button"
        data-testid={testid}
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-2 border-2 border-ink px-3 py-2 font-mono text-sm bg-white disabled:bg-secondary/60 text-left"
      >
        <Search className="w-3.5 h-3.5 shrink-0" />
        <span data-testid={`${testid}-value`} className="flex-1 truncate">
          {selected ? label(selected) : (emptyLabel || placeholder)}
        </span>
        <ChevronDown className="w-3.5 h-3.5 shrink-0" />
      </button>

      {open && (
        <div data-testid={`${testid}-panel`}
             className="absolute z-50 left-0 right-0 mt-1 bg-white border-2 border-ink shadow-hard max-h-80 overflow-y-auto">
          <div className="p-2 border-b-2 border-ink sticky top-0 bg-white flex items-center gap-2">
            <input
              autoFocus
              data-testid={`${testid}-search`}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Escape") setOpen(false);
                if (e.key === "Enter") {
                  const first = recentRows[0] || rows[0];
                  if (first) pick(first.id);
                }
              }}
              placeholder={placeholder}
              className="flex-1 border-2 border-ink px-2 py-1 font-mono text-xs outline-none"
            />
            {q && (
              <button type="button" data-testid={`${testid}-clear-search`} onClick={() => setQ("")}
                      className="border-2 border-ink p-1 hover:bg-ink hover:text-white"><X className="w-3 h-3" /></button>
            )}
          </div>

          {emptyLabel && (
            <button type="button" data-testid={`${testid}-clear-row`} onClick={() => pick("")}
                    className="w-full text-left px-3 py-2 border-b border-ink/10 hover:bg-secondary font-mono text-xs">
              {emptyLabel}
            </button>
          )}

          {recentRows.length > 0 && (
            <>
              <div className="px-3 py-1.5 bg-secondary font-mono text-[9px] uppercase tracking-widest"
                   data-testid={`${testid}-recent-header`}>Most recent</div>
              {recentRows.map((e) => <Row key={`r-${e.id}`} e={e} isRecent />)}
            </>
          )}

          {rows.length > 0 && recentRows.length > 0 && (
            <div className="px-3 py-1.5 bg-secondary font-mono text-[9px] uppercase tracking-widest">All employees</div>
          )}
          {rows.map((e) => <Row key={e.id} e={e} />)}

          {recentRows.length === 0 && rows.length === 0 && (
            <div data-testid={`${testid}-no-results`} className="px-3 py-4 font-mono text-xs text-muted-foreground">
              No employee matches “{q}”.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
